import { useEffect, useMemo } from 'react'
import {
  PurchaseProductDetailsAddonIngredient,
  PurchaseProductDetailsAddonProduct,
  PurchaseProductDetailsAddonProductVariant,
} from '@ancon/wildcat-types'
import { useController, useFormContext } from 'react-hook-form'
import useTranslation from 'next-translate/useTranslation'
import { getFormattedCurrency } from '@ancon/wildcat-utils/currency'
import {
  ConfigureAddonGroupFormState,
  ConfigureAddonIngredientFormState,
  ConfigureAddonProductFormState,
} from '@ancon/wildcat-utils/product/types'
import { SelectableType } from '@ancon/wildcat-ui/web/orderweb/Selectable'
import isDetailedProductVariantOutOfStock from '@ancon/wildcat-utils/inventory/isDetailedProductVariantOutOfStocks'

import ProductSelection from '../../components/ProductSelection'
import { outletSelectedOutletIdSelector } from '../../../outlet/store/outletSelector'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import getPriceLabelForVariant from '../../utils/getPriceLabelForAddonProductVariant'

import styles from './RadioButtonTypeAddonGroup.module.scss'

type RadioButtonTypeAddonGroupProps = {
  ingredients: PurchaseProductDetailsAddonIngredient[]
  addOnIndex: number
  products: PurchaseProductDetailsAddonProduct[]
}

type RadioButtonAddonProductProps = {
  product: PurchaseProductDetailsAddonProduct
  productIndex: number
  addOnIndex: number
  selected: boolean
  hasFreeAddons: boolean
  onSelect: (productId: string) => void
}

function RadioButtonAddonProduct({
  product,
  productIndex,
  addOnIndex,
  selected,
  hasFreeAddons,
  onSelect,
}: RadioButtonAddonProductProps) {
  const { t } = useTranslation('common')
  const outletId = useAppSelector(outletSelectedOutletIdSelector)

  const freeProductAmountFieldName = `selectedAddons.${addOnIndex}.products.${productIndex}.freeAmount`
  const addOnSelectedCountFieldName = `selectedAddons.${addOnIndex}.selectedCount`
  const addOnSettingsFieldName = `selectedAddons.${addOnIndex}.addOnSettings`

  const { watch } = useFormContext()

  const [freeAmount, selectedAddOnCount, addOnSettings] = useMemo(
    () =>
      watch([
        freeProductAmountFieldName,
        addOnSelectedCountFieldName,
        addOnSettingsFieldName,
      ]),
    [
      addOnSelectedCountFieldName,
      addOnSettingsFieldName,
      freeProductAmountFieldName,
      watch,
    ],
  )

  const isAddedAsFreeItem = freeAmount === 1
  const isWithinFreeLimit =
    hasFreeAddons && addOnSettings.freeAmount > selectedAddOnCount
  const showFreeLabel = isWithinFreeLimit || isAddedAsFreeItem

  // Persist variant choice per product row
  const {
    field: { value: selectedVariantId, onChange: onVariantChange },
  } = useController({
    name: `selectedAddons.${addOnIndex}.products.${productIndex}.selectedVariantId`,
    defaultValue: product.variants?.[0]?.id ?? null,
  })

  // todo secondary condition don't auto set to first variant but check stock and set to available
  const currentVariant: PurchaseProductDetailsAddonProductVariant | undefined =
    product.variants?.find(v => v.id === selectedVariantId) ??
    product.variants?.[0]

  function priceLabelForProduct() {
    if (product.isBlocked) {
      return t('components.productDetailsModal.unavailable')
    }

    return hasFreeAddons
      ? t('components.productDetailsModal.free')
      : getFormattedCurrency(
          currentVariant?.price.unitPrice ?? 0,
          currentVariant?.price.unitPriceCurrency,
        )
  }

  const productPriceLabel = priceLabelForProduct()

  function isProductOutOfStock() {
    if (product.hasVariants && product.variants?.length) {
      return product.variants.every(variant =>
        isDetailedProductVariantOutOfStock(variant, outletId!),
      )
    }
    return false
  }

  const productNameWithStock = isProductOutOfStock()
    ? `${product.name} (${t('components.productDetailsModal.variantOutOfStock')})`
    : product.name

  return (
    <>
      <ProductSelection
        label={productNameWithStock}
        key={product.id}
        selected={selected}
        disabled={isProductOutOfStock() || product.isBlocked}
        selectionType={SelectableType.RadioButton}
        onChange={checked => (checked ? onSelect(product.id) : undefined)}
        priceLabel={productPriceLabel}
      />

      {product.hasVariants && !!product.variants?.length && (
        <div>
          {selected &&
            product.variants.map(variant => {
              const isOutOfStock = isDetailedProductVariantOutOfStock(
                variant,
                outletId!,
              )

              const variantPriceLabel = getPriceLabelForVariant(
                variant,
                showFreeLabel,
                hasFreeAddons,
                addOnSettings.afterFreePrice.amount,
                product.isBlocked,
                isOutOfStock,
                t,
              )

              return (
                <div className={styles.variantContainer} key={variant.id}>
                  <ProductSelection
                    label={variant.name}
                    priceLabel={variantPriceLabel}
                    disabled={!selected || isOutOfStock || product.isBlocked}
                    selectionType={SelectableType.RadioButton}
                    selected={selected && selectedVariantId === variant.id}
                    onChange={() => onVariantChange(variant.id)}
                  />
                </div>
              )
            })}
        </div>
      )}
    </>
  )
}

export default function RadioButtonTypeAddonGroup({
  ingredients,
  addOnIndex,
  products,
}: RadioButtonTypeAddonGroupProps) {
  const { t } = useTranslation('common')
  const { trigger, setValue, watch, getValues } = useFormContext()

  const addonGroupField = `selectedAddons.${addOnIndex}`
  const addonSelectedCountField = `${addonGroupField}.selectedCount`
  const addonSettingsField = `${addonGroupField}.addOnSettings`
  const addonIngredientsField = `${addonGroupField}.ingredients`
  const addonProductsField = `${addonGroupField}.products`

  const addonSelectedIngredientIdField = `${addonGroupField}.selectedIngredientId`
  const addonSelectedProductIdField = `${addonGroupField}.selectedProductId`

  const addonSettings = watch(addonSettingsField)
  const hasFreeAddons = !!addonSettings?.freeAmount

  const {
    field: { value: selectedIngredientId, onChange: onSelectIngredientId },
  } = useController({
    name: addonSelectedIngredientIdField,
  })

  const {
    field: { value: selectedProductId, onChange: onSelectProductId },
  } = useController({
    name: addonSelectedProductIdField,
  })

  useController({
    name: addonGroupField,
    rules: {
      validate: (value: ConfigureAddonGroupFormState) => {
        const { selectedCount: selectedAddonCount } = value

        if (selectedAddonCount === 0) {
          return t('components.productDetailsModal.mandatoryChoice').toString()
        }

        return true
      },
    },
  })

  useEffect(() => {
    // Trigger Initial Validation Cycle
    trigger(addonGroupField)
  }, [addonGroupField, trigger])

  function handleAddonIngredientChange(ingredientId: string) {
    // choose ingredient, clear product selection
    onSelectIngredientId(ingredientId)
    onSelectProductId(null)

    // radio -> exactly one item selected in group
    setValue(addonSelectedCountField, 1)

    // Mark only this ingredient as selected/free; clear all products
    const ingredientsFormState: ConfigureAddonIngredientFormState[] = getValues(
      addonIngredientsField,
    )

    setValue(
      addonIngredientsField,
      (ingredientsFormState ?? []).map(ing => {
        const isSelected = ing.id === ingredientId
        return {
          ...ing,
          selectedCount: isSelected ? 1 : 0,
          freeAmount: hasFreeAddons && isSelected ? 1 : 0,
        }
      }),
    )

    const productFormState: ConfigureAddonProductFormState[] =
      getValues(addonProductsField)
    setValue(
      addonProductsField,
      (productFormState ?? []).map(p => ({
        ...p,
        selectedCount: 0,
        freeAmount: 0,
      })),
    )

    // Trigger addon group validation
    trigger(addonGroupField)
  }

  function handleAddonProductChange(productId: string) {
    // choose product, clear ingredient selection
    onSelectProductId(productId)
    onSelectIngredientId(null)

    setValue(addonSelectedCountField, 1)

    // Mark only this product as selected/free; clear all ingredients
    const productFormState: ConfigureAddonProductFormState[] =
      getValues(addonProductsField)
    setValue(
      addonProductsField,
      (productFormState ?? []).map(p => {
        const isSelected = p.id === productId
        return {
          ...p,
          selectedCount: isSelected ? 1 : 0,
          freeAmount: hasFreeAddons && isSelected ? 1 : 0,
        }
      }),
    )

    const ingredientsFormState: ConfigureAddonIngredientFormState[] = getValues(
      addonIngredientsField,
    )
    setValue(
      addonIngredientsField,
      (ingredientsFormState ?? []).map(ing => ({
        ...ing,
        selectedCount: 0,
        freeAmount: 0,
      })),
    )

    // Trigger addon group validation
    trigger(addonGroupField)
  }

  return (
    <>
      {ingredients.map(({ ingredientName, id, price, currency }) => (
        <ProductSelection
          label={ingredientName}
          key={id}
          selected={selectedIngredientId === id}
          selectionType={SelectableType.RadioButton}
          onChange={checked =>
            checked ? handleAddonIngredientChange(id) : undefined
          }
          priceLabel={
            hasFreeAddons || price === 0
              ? t('components.productDetailsModal.free')
              : getFormattedCurrency(price, currency)
          }
        />
      ))}

      {products.map((product, i) => (
        <RadioButtonAddonProduct
          key={product.id}
          product={product}
          productIndex={i}
          addOnIndex={addOnIndex}
          selected={selectedProductId === product.id}
          hasFreeAddons={hasFreeAddons}
          onSelect={handleAddonProductChange}
        />
      ))}
    </>
  )
}
